import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, getChartsBaseUrl, post, put } from "./request";

export function useExportComplianceSchedule(onSuccess?: any, onError?: any) {
    const { mutate: exportComplianceSchedule, error, isLoading: exporting } = useMutation(
        ['exportComplianceSchedule'],
        async (payload: any) => await post('/api/v1/compliances/export_audit_schedule', {export_audit: payload}, null, true, { responseType: 'blob' }),
        {
            onError,
            onSuccess: (response) => {
                const data = (response || {});
                onSuccess(data);
            }
        }
    );
    return { exportComplianceSchedule, error, exporting };
}

export function useImportComplianceSchedule(onSuccess?: any, onError?: any) {
    const { mutate: uploadFile, error, isLoading: uploading } = useMutation(
        ['importComplianceSchedule'],
        async (formData: any) => await post(`/api/v1/compliances/import_audit_schedule`, formData, null, true, { 
            responseType: 'blob'
        }),
        {
            onError,
            onSuccess: (response) => {
                const data = (response || {});
                onSuccess(data);
            }
        }
    );
    return { uploadFile, error, uploading };
}

export function getAllComplianceActivies(payload: any) {
    return get('/api/v1/compliances', payload)
}

export function useGetAllComplianceActivities(payload: any, enabled = true) {
    const { data, isFetching, refetch } = useQuery(
        ['complianceActivities', payload],
        async () => await getAllComplianceActivies(payload),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled
        }
    );
    let items = (data || { data: {} }).data.items;
    let meta = (data || { data: { meta: {} } }).data.meta;
    
    return {
        activities: items,
        total: meta.total_count,
        statusCount: meta.status_count,
        isFetching, refetch
    };
}

export function useGetComplianceById(id: any, payload = {}) {
    const queryClient = useQueryClient();
    function invalidate() {
        queryClient.invalidateQueries(['complianceById', id, payload])
    }
    const { data, isFetching, refetch } = useQuery(
        ['complianceById', id, payload],
        async () => await get(`/api/Compliance/Get/${id}`, payload),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: Boolean(id)
        }
    );

    return { activity: ((data || {}).data || null) as any, isFetching, refetch, invalidate };
}

export function useDeleteComplianceSchedule(onSuccess?: any, onError?: any) {
    const { mutate: deleteComplianceSchedule, error, isLoading: deleting } = useMutation(
        ['deleteComplianceSchedule'],
        async (payload: any) => await del(`/api/v1/compliances/${payload.id}`, payload),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response || {});
            }
        }
    );
    return { deleteComplianceSchedule, error, deleting };
}

export function useDeleteComplianceDocument(onSuccess?: any, onError?: any) {
    const { mutate: deleteDocument, error, isLoading: deleting } = useMutation(
        ['deleteDocument'],
        async (id: any) => await del(`/api/v1/compliance_details/${id}`),
        {
            onError,
            onSuccess: (response) => {
                const data = (response || {}).data || {};
                onSuccess(data);
            }
        }
    );
    return { deleteDocument, error, deleting };
}

export function useBulkUpdateComplianceSchedule(onSuccess?: any, onError?: any) {
    const { mutate: updateBulkComplianceSchedule, error, isLoading: updating } = useMutation(
        ['updateBulkComplianceSchedule'],
        async (payload) => await put(`/api/Compliance/BulkUpdate`, payload),
        {
            onError,
            onSuccess: (response) => {
                const data = (response || {}).data || {};
                onSuccess(data);
            }
        }
    );
    return { updateBulkComplianceSchedule, error, updating };
}

export function useUpdateComplianceSchedule(onSuccess?: any, onError?: any) {
    const { mutate: updateComplianceSchedule, error, isLoading: updating } = useMutation(
        ['updateComplianceSchedule'],
        async (payload: any) => await put(`/api/v1/compliances/${payload.compliance.id}`, payload),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response || {});
            }
        }
    );
    return { updateComplianceSchedule, error, updating };
}


export function useSubmitComplianceActivity(onSuccess?: any, onError?: any) {
    const { mutate: submitComplianceActivity, error, isLoading: submitting } = useMutation(
        ['submitComplianceActivity'],
        async (keys: any) => await post(`/api/Compliance/SubmitToAudit`, keys),
        {
            onError,
            onSuccess: (response) => {
                const data = (response || {}).data || {};
                onSuccess(data);
            }
        }
    );
    return { submitComplianceActivity, error, submitting };
}

export function useGetComplianceActivityDocuments(payload: any = {}) {
    const queryClient = useQueryClient();
    function invalidate() {
        queryClient.invalidateQueries(['complianceActivityDocuments', payload])
    }
    const { data, isFetching, refetch } = useQuery(
        ['complianceActivityDocuments', payload],
        async () => await get('/api/v1/compliance_details', payload),
        {
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            enabled: Boolean(((payload || {}).filters || {}).compliance_id_eq)
        }
    );
    let items = (data || { data: {} }).data.items;
    let meta = (data || { data: { meta: {} } }).data.meta;
    return { documents: items || [], isFetching, invalidate, refetch };
}

export function useGetComplianceByDate(payload: any, enabled = true) {
    const { data, isFetching, refetch } = useQuery(
        ['complianceByDate', payload],
        async () => await post('/api/Compliance/GetAllGroupByDate', payload),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled
        }
    );

    return { groups: (data || {}).data || [], isFetching, refetch };
}

export function useUploadDocument(onSuccess?: any, onError?: any) {
    const { mutate: uploadDocument, error, isLoading: uploading } = useMutation(
        ['uploadFile'],
        async ({ id, formData }: any) => await post(`/api/v1/compliance_details/upload`, formData, null, true),
        {
            onError,
            onSuccess: (response) => {
                const data = (response || {});
                onSuccess(data);
            }
        }
    );
    return { uploadDocument, error, uploading };
}

export function useBulkUploadDocuments(onSuccess?: any, onError?: any) {
    const { mutate: bulkUploadDocuments, error, isLoading: uploading } = useMutation(
        ['bulkUploadFile'],
        async (formData: any) => await post(`/api/v1/compliance_details/bulk_upload`, formData, null, true),
        {
            onError,
            onSuccess: (response) => {
                const data = (response || {});
                onSuccess(data);
            }
        }
    );
    return { bulkUploadDocuments, error, uploading };
}

export function useRecallCompliance(onSuccess?: any, onError?: any) {
    const { mutate: recallCompliance, error, isLoading: recalling } = useMutation(
        ['recallCompliance'],
        async ({ id, recall_remarks, due_date }: any) => await post(`/api/v1/compliances/${id}/recall`, { recall_remarks, due_date }),
        {
            onError,
            onSuccess: (response) => {
                const data = (response || {});
                onSuccess(data);
            }
        }
    );
    return { recallCompliance, error, recalling };
}

export function useBulkReviewCompliances(onSuccess?: any, onError?: any) {
    const { mutate: bulkReviewCompliances, error, isLoading: reviewing } = useMutation(
        ['bulkReviewCompliances'],
        async (payload: any) => await post(`/api/v1/compliances/bulk_update`, payload),
        {
            onError,
            onSuccess: (response) => {
                const data = (response || {});
                onSuccess(data);
            }
        }
    );
    return { bulkReviewCompliances, error, reviewing };
}

export function useGetOverallComplianceStatus(payload: any, enabled = true) {
    const { data, isFetching, refetch } = useQuery(
        ['overallComplianceStatus', { ...payload, pagination: null }],
        async () => await post('/api/Compliance/GetByStatus', { ...payload, pagination: null }),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled
        }
    );

    return { response: (data || {}).data || [], isFetching, refetch };
}

export function useGetComplianceStatusByCategory(category: string, payload: any, enabled = true) {
    const { data, isFetching, refetch } = useQuery(
        ['complianceStatusByCategory', category, payload],
        async () => await post(`/api/Compliance/GetByCategory?category=${category}`, payload),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled
        }
    );

    return { response: (data || {}).data || [], isFetching, refetch };
}

export function useGetComplianceActivityTypeStats(payload: any, enabled = true) {
    const { data, isFetching, refetch } = useQuery(
        ['complianceActivityTypeStats', payload],
        async () => await post('/api/v1/compliances/compliance_stats', payload),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled
        }
    );
    return { stats: (data || {}).data || [], isFetching, refetch };
}

export function useGetComplianceDashboardChartsStats(payload: any, enabled = true) {
    const { data, isFetching, refetch } = useQuery(
        ['complianceDashboardChartsStats', payload],
        async () => await post('/api/v1/compliances/compliance_chart_stats', payload),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled
        }
    );
    return { stats: (data || {}).data || [], isFetching, refetch };
}

export function useExportComplianceDashbard(onSuccess?: any, onError?: any) {
    const { mutate: exportDashboard, error, isLoading: exporting } = useMutation(
        ['auditReport'],
        async (payload: any) => await post(`${getChartsBaseUrl()}/Audit/GetDashboardChart`, payload, null, true, { responseType: 'blob' }),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response || {});
            }
        }
    );
    return { exportDashboard, error, exporting };
}

export function useExportComplianceReport(onSuccess?: any, onError?: any) {
  const { mutate: exportReport, error, isLoading: exporting } = useMutation(
      ['auditReportCompliance'],
      async (payload: any) => await get(`/api/v1/compliances/download_audit_report.pdf`, payload, null, { responseType: 'blob' }),
      {
          onError,
          onSuccess: (response: any) => {
              const data = (response || {});
              onSuccess(data);
          }
      }
  );
  return { exportReport, error, exporting };
}

export function useExportComplianceActivities(onSuccess?: any, onError?: any) {
    const { mutate: exportComplianceActivities, error, isLoading: exporting } = useMutation(
        ['exportComplianceActivities'],
        async (payload: any) => await post('/api/v1/compliances/export', {compliances: payload}, null, true, { responseType: 'blob' }),
        {
            onError,
            onSuccess: (response) => {
                const data = (response || {});
                onSuccess(data);
            }
        }
    );
    return { exportComplianceActivities, error, exporting };
}

export function useAddNotice(onSuccess?: any, onError?: any) {
    const { mutate: addNotice, error, isLoading: adding } = useMutation(
        ['addNotice'],
        async (payload: any) => await post('/api/v1/compliances', payload),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response);
            }
        }
    );
    return { addNotice, error, adding };
}

export function useUpdateNotice(onSuccess?: any, onError?: any) {
    const { mutate: updateNotice, error, isLoading: updating } = useMutation(
        ['updateNotice'],
        async (payload: any) => await put(`/api/v1/compliances/${payload.id}`, payload),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response);
            }
        }
    );
    return { updateNotice, error, updating };
}

export function useBulkAssignCompliances(onSuccess?: any, onError?: any) {
    const { mutate: bulkAssignCompliances, error, isLoading: assigning } = useMutation(
        ['bulkAssignCompliances'],
        async (payload: any) => await post('/api/v1/compliances/bulk_assign', payload),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response || {});
            }
        }
    );
    return { bulkAssignCompliances, error, assigning };
}